import { Badge, Button, Card } from '@ohear/ui'
/**
 * Microphone test card.
 *
 * Drives a Web Audio AnalyserNode (no AudioWorklet — we only need cheap peak
 * + RMS, not raw samples) for ~5 seconds, then reports peak / RMS / approximate
 * noise floor in dBFS. The test passes if peak < -6 dBFS and noise floor < -55
 * dBFS, matching the OHEAR home-studio target.
 *
 * The browser DSP (echo cancel / AGC / noise suppression) is disabled exactly
 * the way the recorder disables it — we want what the actor's mic actually
 * produces.
 */
import { useEffect, useRef, useState } from 'react'
import { useSaveDeviceTestResults } from '../../lib/api/account'

interface Reading {
  peakDb: number
  rmsDb: number
  noiseDb: number
}

type Severity = 'pass' | 'warn' | 'fail'

/**
 * Pass / warn / fail thresholds. We're deliberately friendlier than a
 * studio standard — anything that won't actively wreck a take should
 * land in pass / warn, not fail. The recorder pipeline's LUFS-targeted
 * gain stage will pull a -12 dBFS take into spec; what we really care
 * about is "no clipping" and "the room isn't a wind tunnel".
 */
function classifyPeak(db: number): Severity {
  if (!Number.isFinite(db)) return 'warn'
  if (db > -1) return 'fail'
  if (db > -3) return 'warn'
  if (db < -30) return 'warn' // too quiet — actor probably standing too far back
  return 'pass'
}
function classifyNoise(db: number): Severity {
  if (!Number.isFinite(db)) return 'warn'
  if (db < -55) return 'pass'
  if (db < -45) return 'warn'
  return 'fail'
}

const TEST_DURATION_MS = 5000
const SILENCE_THRESHOLD_DB = -45 // samples below this contribute to noise floor

export function MicTest() {
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [livePeakDb, setLivePeakDb] = useState<number>(Number.NEGATIVE_INFINITY)
  const [reading, setReading] = useState<Reading | null>(null)
  const saveResults = useSaveDeviceTestResults()

  const ctxRef = useRef<AudioContext | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const rafRef = useRef<number | null>(null)
  const startedAtRef = useRef<number>(0)
  const peakRef = useRef<number>(0)
  const rmsAccRef = useRef<number>(0)
  const rmsCountRef = useRef<number>(0)
  const noiseAccRef = useRef<number>(0)
  const noiseCountRef = useRef<number>(0)

  useEffect(() => {
    return () => {
      cleanup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cleanup = () => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    if (ctxRef.current && ctxRef.current.state !== 'closed') {
      void ctxRef.current.close()
    }
    ctxRef.current = null
  }

  const start = async () => {
    setError(null)
    setReading(null)
    setLivePeakDb(Number.NEGATIVE_INFINITY)
    peakRef.current = 0
    rmsAccRef.current = 0
    rmsCountRef.current = 0
    noiseAccRef.current = 0
    noiseCountRef.current = 0

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: false,
          autoGainControl: false,
          noiseSuppression: false,
        },
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Microphone access denied')
      return
    }

    const ctx = new AudioContext()
    const source = ctx.createMediaStreamSource(stream)
    const analyser = ctx.createAnalyser()
    analyser.fftSize = 2048
    analyser.smoothingTimeConstant = 0
    source.connect(analyser)

    ctxRef.current = ctx
    streamRef.current = stream
    startedAtRef.current = performance.now()
    setRunning(true)

    const buf = new Float32Array(analyser.fftSize)
    const tick = () => {
      analyser.getFloatTimeDomainData(buf)
      let frameMaxAbs = 0
      let sumSquares = 0
      for (let i = 0; i < buf.length; i++) {
        const v = buf[i] ?? 0
        const a = Math.abs(v)
        if (a > frameMaxAbs) frameMaxAbs = a
        sumSquares += v * v
      }
      const rmsLinear = Math.sqrt(sumSquares / buf.length)
      const frameRmsDb = linToDb(rmsLinear)
      const framePeakDb = linToDb(frameMaxAbs)
      if (frameMaxAbs > peakRef.current) peakRef.current = frameMaxAbs
      rmsAccRef.current += rmsLinear * rmsLinear
      rmsCountRef.current += 1
      // Treat sub-threshold frames as silence for noise floor estimate.
      if (framePeakDb < SILENCE_THRESHOLD_DB) {
        noiseAccRef.current += rmsLinear * rmsLinear
        noiseCountRef.current += 1
      }
      setLivePeakDb(framePeakDb)

      const elapsed = performance.now() - startedAtRef.current
      if (elapsed < TEST_DURATION_MS) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        finish()
      }
    }

    const finish = () => {
      const peakDb = linToDb(peakRef.current)
      const rmsDb =
        rmsCountRef.current === 0
          ? Number.NEGATIVE_INFINITY
          : linToDb(Math.sqrt(rmsAccRef.current / rmsCountRef.current))
      const noiseDb =
        noiseCountRef.current === 0
          ? Number.NEGATIVE_INFINITY
          : linToDb(Math.sqrt(noiseAccRef.current / noiseCountRef.current))
      setReading({ peakDb, rmsDb, noiseDb })
      setRunning(false)
      cleanup()

      // Persist to user.last_device_test_results. Skip the write if either
      // measurement is non-finite (mic delivered no samples — likely a
      // browser permission edge case), since the server validator rejects
      // non-finite numbers anyway.
      if (Number.isFinite(peakDb) && Number.isFinite(noiseDb)) {
        saveResults.mutate({
          mic: {
            peakDbfs: round1(peakDb),
            noiseFloorDbfs: round1(noiseDb),
            rmsDbfs: Number.isFinite(rmsDb) ? round1(rmsDb) : undefined,
            durationSec: TEST_DURATION_MS / 1000,
            testedAt: new Date().toISOString(),
          },
        })
      }
    }

    rafRef.current = requestAnimationFrame(tick)
  }

  const stop = () => {
    setRunning(false)
    cleanup()
  }

  const liveLevelPct =
    livePeakDb === Number.NEGATIVE_INFINITY
      ? 0
      : Math.max(0, Math.min(100, ((livePeakDb + 60) / 60) * 100))
  const peakSev = reading ? classifyPeak(reading.peakDb) : null
  const noiseSev = reading ? classifyNoise(reading.noiseDb) : null
  // Aggregate verdict: hard fail wins, then warn, then pass. We never show
  // a red badge at the top unless something will actually break a take.
  const verdict: Severity | null = !reading
    ? null
    : peakSev === 'fail' || noiseSev === 'fail'
      ? 'fail'
      : peakSev === 'warn' || noiseSev === 'warn'
        ? 'warn'
        : 'pass'

  return (
    <Card title="Microphone test">
      <div className="flex flex-col gap-3">
        <p className="text-[13px] text-[var(--text-2)] m-0">
          Speak normally for 5 seconds. We'll measure your peak, RMS, and approximate noise floor.
        </p>

        <div
          role="meter"
          aria-label="Microphone input level"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(liveLevelPct)}
          className="h-3 w-full rounded-[var(--radius-sm)] bg-[var(--bg-3)] overflow-hidden"
        >
          <div
            className="h-full transition-[width] duration-75"
            style={{
              width: `${liveLevelPct}%`,
              background:
                livePeakDb > -3 ? 'var(--red)' : livePeakDb > -6 ? 'var(--amber)' : 'var(--green)',
            }}
          />
        </div>
        <div className="text-[12px] text-[var(--text-2)] tabular-nums">
          Live peak: {fmtDb(livePeakDb)}
        </div>

        <div className="flex items-center gap-2">
          {running ? (
            <Button variant="ghost" onClick={stop}>
              Stop
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => {
                void start()
              }}
            >
              Start mic test
            </Button>
          )}
          {reading && verdict ? (
            <Badge qcSeverity={verdict}>
              {verdict === 'pass'
                ? 'Looks great'
                : verdict === 'warn'
                  ? 'Usable, with notes'
                  : 'Needs attention'}
            </Badge>
          ) : null}
          {saveResults.isPending ? (
            <span className="text-[12px] text-[var(--text-2)]" aria-live="polite">
              Saving...
            </span>
          ) : null}
        </div>

        {error ? <p className="text-[13px] text-[var(--red)] m-0">{error}</p> : null}

        {reading && peakSev && noiseSev ? (
          <>
            <dl className="grid grid-cols-3 gap-3 text-[13px] mt-2">
              <Stat label="Peak" value={fmtDb(reading.peakDb)} severity={peakSev} />
              <Stat
                label="RMS"
                value={fmtDb(reading.rmsDb)}
                severity={reading.rmsDb < -12 ? 'pass' : 'warn'}
              />
              <Stat label="Noise floor" value={fmtDb(reading.noiseDb)} severity={noiseSev} />
            </dl>
            <p className="text-[12px] text-[var(--text-2)] m-0">
              {micAdviceFor(peakSev, noiseSev)}
            </p>
          </>
        ) : null}
      </div>
    </Card>
  )
}

function Stat({
  label,
  value,
  severity,
}: {
  label: string
  value: string
  severity: Severity
}) {
  const color =
    severity === 'pass'
      ? 'text-[var(--green)]'
      : severity === 'warn'
        ? 'text-[var(--amber)]'
        : 'text-[var(--red)]'
  return (
    <div>
      <dt className="text-[12px] text-[var(--text-2)] m-0">{label}</dt>
      <dd className={`m-0 font-medium tabular-nums ${color}`}>{value}</dd>
    </div>
  )
}

function linToDb(linear: number): number {
  if (linear <= 0) return Number.NEGATIVE_INFINITY
  return 20 * Math.log10(linear)
}

function fmtDb(db: number): string {
  if (!Number.isFinite(db)) return '-inf dB'
  return `${db.toFixed(1)} dBFS`
}

function round1(n: number): number {
  return Math.round(n * 10) / 10
}

function micAdviceFor(peak: Severity, noise: Severity): string {
  if (peak === 'fail')
    return 'Your input is clipping. Move further from the mic or lower your gain.'
  if (noise === 'fail')
    return 'High background noise. Try closing windows, turning off fans, or moving to a smaller room.'
  if (peak === 'warn' && noise === 'warn')
    return 'Borderline on level and noise. A pop filter and a quieter room will go a long way.'
  if (peak === 'warn') return 'A small gain adjustment should land you in the sweet spot.'
  if (noise === 'warn')
    return 'Slight room noise. Recordings will be fine, but a treated space sounds even better.'
  return "Your studio sounds ready. We'll remember this for onboarding."
}
